import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { addCartItem, getCart, mergeCart, removeCartItem, updateCartItem } from "@services/shop.service";

export type CartItem = {
  id: string;
  quantity: number;
  line_total: number;
  product: {
    id: string;
    name: string;
    price: number;
    stock: number;
    image_url: string;
  };
};

export type CartState = {
  id: string;
  items: CartItem[];
  subtotal: number;
  itemCount: number;
  status: "idle" | "loading";
};

const emptyCart = {
  id: "",
  items: [],
  subtotal: 0,
  item_count: 0,
};

function toState(cart: any): Omit<CartState, "status"> {
  const next = cart || emptyCart;
  return {
    id: next.id || "",
    items: next.items || [],
    subtotal: Number(next.subtotal || 0),
    itemCount: Number(next.item_count || 0),
  };
}

export const fetchCart = createAsyncThunk("cart/fetch", async () => {
  const cart = await getCart();
  return toState(cart);
});

export const addToCart = createAsyncThunk(
  "cart/add",
  async (payload: { product_id: string; quantity?: number }) => {
    const cart = await addCartItem(payload);
    return toState(cart);
  }
);

export const changeCartItem = createAsyncThunk(
  "cart/change",
  async (payload: { id: string; quantity: number }) => {
    const cart = await updateCartItem(payload.id, { quantity: payload.quantity });
    return toState(cart);
  }
);

export const mergeGuestCart = createAsyncThunk("cart/merge", async () => {
  const cart = await mergeCart();
  return toState(cart);
});

export const deleteCartItem = createAsyncThunk("cart/delete", async (id: string) => {
  const cart = await removeCartItem(id);
  return toState(cart);
});

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    id: "",
    items: [],
    subtotal: 0,
    itemCount: 0,
    status: "idle",
  } as CartState,
  reducers: {},
  extraReducers: (builder) => {
    const saveCart = (
      state: CartState,
      action: { payload: Omit<CartState, "status"> }
    ) => {
      state.id = action.payload.id;
      state.items = action.payload.items;
      state.subtotal = action.payload.subtotal;
      state.itemCount = action.payload.itemCount;
      state.status = "idle";
    };
    builder
      .addCase(fetchCart.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCart.fulfilled, saveCart)
      .addCase(addToCart.fulfilled, saveCart)
      .addCase(changeCartItem.fulfilled, saveCart)
      .addCase(deleteCartItem.fulfilled, saveCart)
      .addCase(mergeGuestCart.fulfilled, saveCart)
      .addCase(fetchCart.rejected, (state) => {
        state.status = "idle";
      })
      .addCase(addToCart.rejected, (state) => {
        state.status = "idle";
      })
      .addCase(changeCartItem.rejected, (state) => {
        state.status = "idle";
      })
      .addCase(deleteCartItem.rejected, (state) => {
        state.status = "idle";
      })
      .addCase(mergeGuestCart.rejected, (state) => {
        state.status = "idle";
      });
  },
});

export default cartSlice.reducer;
